<template>
  <div>
  <v-card elevation="0" class="pa-4">
    <v-col cols="12">
      <h1 class="mb-2">Comprovantes de Veiculação</h1>
      <v-card outlined>

        <v-card-title class="d-flex align-center justify-space-between flex-wrap">
          <div class="d-flex align-center flex-wrap" style="gap: 8px;">
            <v-text-field
              v-model="searchText"
              dense
              outlined
              hide-details
              label="Buscar (número do plano ou inventário)"
              prepend-inner-icon="mdi-magnify"
              clearable
              color="primary"
              style="min-width: 360px;"
              class="primary--text">
            </v-text-field>

            <v-autocomplete
              v-model="selectedPublisher"
              :items="publisherOptions"
              item-text="text"
              item-value="value"
              dense
              outlined
              hide-details
              clearable
              prepend-inner-icon="mdi-radio-tower"
              color="primary"
              label="Inventário"
              style="min-width: 260px; max-width: 340px;"
              class="primary--text">
            </v-autocomplete>

            <v-select
              v-model="selectedStatus"
              :items="statusOptions"
              item-text="text"
              item-value="value"
              dense
              outlined
              hide-details
              prepend-inner-icon="mdi-filter-outline"
              color="primary"
              label="Status"
              style="max-width: 200px;"
              class="primary--text">
            </v-select>

            <v-btn
              :disabled="!hasFilterChanges"
              :color="hasFilterChanges ? 'primary' : 'grey'"
              small
              @click="applyFilters">
              Aplicar Filtros
            </v-btn>

            <v-btn
              color="red"
              dark
              small
              @click="clearFilters">
              Limpar Filtros
            </v-btn>
          </div>

          <span class="text-body-2 grey--text">
            Total: {{ filteredPlans.length }}
          </span>
        </v-card-title>

        <v-data-table
          :headers="headers"
          :items="filteredPlans"
          :loading="loading"
          :items-per-page="10"
          loading-text="Carregando comprobatórios..."
          show-expand
          item-key="groupKey"
          :expanded.sync="expanded"
          class="elevation-0 clickable-rows"
          @click:row="(item, row) => row.expand(!row.isExpanded)">

          
          <template v-slot:[`item.data-table-expand`]="{ isExpanded, expand }">
            <v-btn icon @click.stop="expand(!isExpanded)" plain>
              <v-icon
                class="expand-icon"
                :class="{ 'expanded': isExpanded }"
                color="grey darken-1">
                mdi-chevron-down
              </v-icon>
            </v-btn>
          </template>

          
          <template v-slot:[`item.gridId`]="{ item }">
            <span class="font-weight-medium">#{{ item.gridId }}</span>
          </template>

          
          <template v-slot:[`item.publisherName`]="{ item }">
            <div v-if="item.publishers && item.publishers.length === 1" class="d-flex align-center justify-center" style="gap: 8px;">
              <v-avatar v-if="item.publishers[0].logo" size="28" tile>
                <v-img :src="item.publishers[0].logo" :alt="item.publishers[0].name" contain />
              </v-avatar>
              <span>{{ item.publishers[0].name }}</span>
            </div>
            <div v-else-if="item.publishers && item.publishers.length > 1" class="d-flex align-center justify-center" style="gap: 6px;">
              <v-icon small color="grey darken-1">mdi-radio-tower</v-icon>
              <span>{{ item.publishers.length }} inventários</span>
            </div>
            <span v-else class="grey--text text-body-2">—</span>
          </template>

          
          <template v-slot:[`item.piCount`]="{ item }">
            <v-chip color="primary" text-color="white" small>
              {{ item.piCount }}
            </v-chip>
          </template>

          
          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              v-if="item.status"
              :color="getStatusColor(item.status)"
              text-color="white"
              small>
              <strong>{{ item.status }}</strong>
            </v-chip>
            <span v-else class="grey--text text-body-2">—</span>
          </template>

          
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-0 expanded-cell">
              <div class="orders-wrapper">

                <div v-if="!item.orders || item.orders.length === 0"
                     class="d-flex align-center justify-center grey--text py-5">
                  <v-icon small class="mr-2" color="grey">mdi-information-outline</v-icon>
                  Nenhuma ordem encontrada para este plano.
                </div>

                <template v-else>
                  
                  <div class="order-row order-row--header d-flex align-center justify-space-between order-row--wrap">
                    <span class="order-col order-col--provider-name text-caption font-weight-bold grey--text text--darken-1 text-uppercase">
                      Nome
                    </span>
                    <span class="order-col order-col--provider-doc text-caption font-weight-bold grey--text text--darken-1 text-uppercase">
                      CNPJ
                    </span>
                    <span class="order-col order-col--publisher text-caption font-weight-bold grey--text text--darken-1 text-uppercase">
                      Inventário
                    </span>
                    <span class="order-col order-col--period text-caption font-weight-bold grey--text text--darken-1 text-uppercase">
                      Período
                    </span>
                    <span class="order-col order-col--docid text-caption font-weight-bold grey--text text--darken-1 text-uppercase">
                      Pedido de Inserção/P.I
                    </span>
                    <span class="order-col order-col--formats text-caption font-weight-bold grey--text text--darken-1 text-uppercase">
                      Formatos
                    </span>
                    <span class="order-col order-col--download text-caption font-weight-bold grey--text text--darken-1 text-uppercase">
                      P.I
                    </span>
                    <span class="order-col order-col--action text-caption font-weight-bold grey--text text--darken-1 text-uppercase">
                      Comprobatório
                    </span>
                    <span class="order-col order-col--status text-caption font-weight-bold grey--text text--darken-1 text-uppercase">
                      Status
                    </span>
                  </div>

                  
                  <div
                    v-for="(order, index) in mergeConsecutiveOrders(item.orders)"
                    :key="order.piId"
                    class="order-row d-flex align-center justify-space-between order-row--wrap"
                    :class="{ 'order-row--last': index === item.orders.length - 1 }">

          
                    <div class="order-row--mobile-compact d-none align-center justify-space-between" style="width:100%; gap:8px;">
                      <div class="d-flex align-center" style="gap:6px; min-width:0; flex:1;">
                        <v-avatar v-if="order.gridProfileLogo" size="22" tile style="flex-shrink:0;">
                          <v-img :src="order.gridProfileLogo" :alt="order.gridProfileDisplayName" contain />
                        </v-avatar>
                        <span class="text-body-2 font-weight-medium text-truncate">{{ order.gridProfileDisplayName || '—' }}</span>
                      </div>

                      <div class="d-flex align-center" style="gap:6px; flex-shrink:0;">
                        <v-chip
                          v-if="order.placementProofDocs && order.placementProofDocs.length > 0"
                          color="#4caf50"
                          text-color="white"
                          x-small>
                          <strong>Aprovado</strong>
                        </v-chip>
                        <v-chip
                          v-else-if="order.statusGlobal"
                          :color="getStatusColor(order.statusGlobal)"
                          text-color="white"
                          x-small>
                          <strong>{{ order.statusGlobal }}</strong>
                        </v-chip>

                        <template v-if="order.placementProofDocs && order.placementProofDocs.length > 0">
                          <v-menu v-if="order.placementProofDocs.length > 1" offset-y bottom left open-on-hover>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn color="success" x-small depressed v-bind="attrs" v-on="on">
                                <v-icon x-small left>mdi-download</v-icon>
                                Comprovantes
                              </v-btn>
                            </template>
                            <v-list dense>
                              <v-list-item
                                v-for="(doc, di) in order.placementProofDocs"
                                :key="doc._id"
                                @click.stop="downloadPlacementProofDoc(doc)">
                                <v-list-item-icon class="mr-2 my-auto">
                                  <v-icon small color="success">mdi-file-download-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                  <v-list-item-title class="text-body-2">{{ (doc.file && doc.file.name) || ('Comprovante ' + (di + 1)) }}</v-list-item-title>
                                </v-list-item-content>
                              </v-list-item>
                            </v-list>
                          </v-menu>
                          <v-btn v-else color="success" x-small depressed @click.stop="downloadPlacementProofDoc(order.placementProofDocs[0])">
                            <v-icon x-small left>mdi-download</v-icon>
                            Comprovante
                          </v-btn>
                        </template>
                        <v-btn v-else color="primary" x-small depressed @click.stop="openComprobatorioList(order)">
                          <v-icon x-small left>mdi-cloud-upload-outline</v-icon>
                          Comprobatórios
                        </v-btn>
                      </div>
                    </div>
                  

                    <div class="order-col order-col--provider-name">
                      <span class="order-col__label">Nome</span>
                      <div class="d-flex align-center" style="gap: 6px; min-width:0;">
                        <v-avatar v-if="order.gridProfileLogo" size="22" tile style="flex-shrink:0;">
                          <v-img :src="order.gridProfileLogo" :alt="order.gridProfileDisplayName" contain />
                        </v-avatar>
                        <v-tooltip top>
                          <template v-slot:activator="{ on, attrs }">
                            <span
                              class="text-body-2 provider-name-clamp font-weight-medium"
                              v-bind="attrs"
                              v-on="on">
                              {{ order.gridProfileDisplayName || '—' }}
                            </span>
                          </template>
                          <span>{{ order.gridProfileDisplayName }}</span>
                        </v-tooltip>

                      
                        <v-menu offset-y left :close-on-content-click="false" max-width="300" class="order-col--info-icon">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn icon x-small v-bind="attrs" v-on="on" @click.stop class="order-col--info-icon">
                              <v-icon small color="grey darken-1">mdi-information-outline</v-icon>
                            </v-btn>
                          </template>
                          <v-card class="pa-3" style="font-size:13px; line-height:1.7;">
                            <div class="order-info-row order-info--formats">
                              <span class="order-info-label">Formatos</span>
                              <span v-if="order.format"><span v-if="order.bucket">{{ order.bucket }}: </span>{{ order.format }}</span>
                              <span v-else class="grey--text">—</span>
                            </div>
                            <div class="order-info-row order-info--doc">
                              <span class="order-info-label">CNPJ</span>
                              <span>{{ formatCnpj(order.providerDocument) }}</span>
                            </div>
                            <div class="order-info-row order-info--period">
                              <span class="order-info-label">Período</span>
                              <span v-if="order.endPeriod">{{ order.period }} → {{ order.endPeriod }}</span>
                              <span v-else>{{ order.period || '—' }}</span>
                            </div>
                            <div class="order-info-row order-info--docid">
                              <span class="order-info-label">Pedido/P.I</span>
                              <span>{{ order.documentId || '—' }}</span>
                            </div>
                            <div class="order-info-row order-info--publisher">
                              <span class="order-info-label">Inventário</span>
                              <div class="d-flex align-center" style="gap:4px;">
                                <v-avatar v-if="order.publisherLogo" size="16" tile><v-img :src="order.publisherLogo" contain /></v-avatar>
                                <span>{{ order.publisherName || '—' }}</span>
                              </div>
                            </div>
                         
                            <div class="pt-2">
                              <v-menu v-if="order.mergedDownloads && order.mergedDownloads.length > 1" offset-y bottom>
                                <template v-slot:activator="{ on: menuOn, attrs }">
                                  <v-btn small text color="primary" v-bind="attrs" v-on="menuOn">
                                    <v-icon small left>mdi-file-download-outline</v-icon>
                                    Baixar P.I ({{ order.mergedDownloads.length }})
                                  </v-btn>
                                </template>
                                <v-list dense>
                                  <v-list-item v-for="d in order.mergedDownloads" :key="d.piId" :disabled="!d.xpath || !!xpathDownloading[d.piId]" @click.stop="downloadXpath(d)">
                                    <v-list-item-icon class="mr-2 my-auto">
                                      <v-progress-circular v-if="xpathDownloading[d.piId]" indeterminate size="16" width="2" color="primary" />
                                      <v-icon v-else small color="primary">mdi-file-download-outline</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-content><v-list-item-title class="text-body-2">{{ d.period || d.piId }}</v-list-item-title></v-list-item-content>
                                  </v-list-item>
                                </v-list>
                              </v-menu>
                              <v-btn v-else small text color="primary" :disabled="!order.xpath" :loading="xpathDownloading[order.piId]" @click.stop="downloadXpath(order)">
                                <v-icon small left>mdi-file-download-outline</v-icon>
                                Baixar P.I
                              </v-btn>
                            </div>
                          </v-card>
                        </v-menu>
                      </div>
                    </div>

                    <div class="order-col order-col--provider-doc">
                      <span class="order-col__label">CNPJ</span>
                      <span class="text-body-2 text-truncate">{{ formatCnpj(order.providerDocument) }}</span>
                    </div>

                    <div class="order-col order-col--publisher">
                      <span class="order-col__label">Inventário</span>
                      <div class="d-flex align-center" style="gap: 6px; min-width:0;">
                        <v-avatar v-if="order.publisherLogo" size="22" tile>
                          <v-img :src="order.publisherLogo" :alt="order.publisherName" contain />
                        </v-avatar>
                        <span class="text-body-2 text-truncate">{{ order.publisherName || '—' }}</span>
                      </div>
                    </div>

                    <div class="order-col order-col--period">
                      <span class="order-col__label">Período</span>
                      <div class="d-flex align-center" style="white-space: nowrap;">
                        <v-icon x-small class="mr-2" color="grey darken-1">mdi-calendar-range</v-icon>
                        <span v-if="order.endPeriod" class="text-body-2">{{ order.period }} → {{ order.endPeriod }}</span>
                        <span v-else class="text-body-2">{{ order.period || '—' }}</span>
                      </div>
                    </div>

                    <div class="order-col order-col--docid">
                      <span class="order-col__label">Pedido / P.I</span>
                      <div class="d-flex align-center" style="gap: 4px;">
                        <span class="text-body-2">{{ order.documentId || '—' }}</span>
                        <v-tooltip v-if="order.mergedDownloads && order.mergedDownloads.length > 1" top max-width="240">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip x-small color="primary" dark v-bind="attrs" v-on="on" style="cursor: default;">
                              +{{ order.mergedDownloads.length - 1 }}
                            </v-chip>
                          </template>
                          <div>
                            <div v-for="d in order.mergedDownloads.slice(1)" :key="d.piId" class="text-caption">
                              {{ d.documentId || d.piId }}
                            </div>
                          </div>
                        </v-tooltip>
                      </div>
                    </div>

                    <div class="order-col order-col--formats">
                      <span class="order-col__label">Formatos</span>
                      <span v-if="order.format" class="text-body-2 text-center"><span v-if="order.bucket">{{ order.bucket }}:</span> {{ order.format }}</span>
                      <span v-else class="grey--text text-body-2 text-center">—</span>
                    </div>

                    <div class="order-col order-col--download">
                      <v-menu v-if="order.mergedDownloads && order.mergedDownloads.length > 1" offset-y bottom left>
                        <template v-slot:activator="{ on: menuOn, attrs }">
                          <v-tooltip top>
                            <template v-slot:activator="{ on: tooltipOn }">
                              <v-btn icon small v-bind="attrs" v-on="{ ...menuOn, ...tooltipOn }">
                                <v-badge :content="order.mergedDownloads.length" color="primary" overlap>
                                  <v-icon small color="primary">mdi-file-download-outline</v-icon>
                                </v-badge>
                              </v-btn>
                            </template>
                            <span>Baixar P.I ({{ order.mergedDownloads.length }})</span>
                          </v-tooltip>
                        </template>
                        <v-list dense>
                          <v-list-item
                            v-for="d in order.mergedDownloads"
                            :key="d.piId"
                            :disabled="!d.xpath || !!xpathDownloading[d.piId]"
                            @click.stop="downloadXpath(d)">
                            <v-list-item-icon class="mr-2 my-auto">
                              <v-progress-circular v-if="xpathDownloading[d.piId]" indeterminate size="16" width="2" color="primary" />
                              <v-icon v-else small color="primary">mdi-file-download-outline</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-body-2">{{ d.period || d.piId }}</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                      <v-tooltip v-else top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            small
                            v-bind="attrs"
                            v-on="on"
                            :loading="xpathDownloading[order.piId]"
                            :disabled="!order.xpath"
                            @click.stop="downloadXpath(order)">
                            <v-icon small color="primary">mdi-file-download-outline</v-icon>
                          </v-btn>
                        </template>
                        <span>Baixar P.I</span>
                      </v-tooltip>
                    </div>

                    <div class="order-col order-col--action">
                      <template v-if="order.placementProofDocs && order.placementProofDocs.length > 0">
                        <v-menu v-if="order.placementProofDocs.length > 1" offset-y bottom left open-on-hover>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn color="success" small depressed v-bind="attrs" v-on="on">
                              <v-icon small left>mdi-download</v-icon>
                              Comprovantes ({{ order.placementProofDocs.length }})
                            </v-btn>
                          </template>
                          <v-list dense>
                            <v-list-item
                              v-for="(doc, di) in order.placementProofDocs"
                              :key="doc._id"
                              @click.stop="downloadPlacementProofDoc(doc)">
                              <v-list-item-icon class="mr-2 my-auto">
                                <v-icon small color="success">mdi-file-download-outline</v-icon>
                              </v-list-item-icon>
                              <v-list-item-content>
                                <v-list-item-title class="text-body-2">{{ (doc.file && doc.file.name) || ('Comprovante ' + (di + 1)) }}</v-list-item-title>
                              </v-list-item-content>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                        <v-btn v-else color="success" small depressed @click.stop="downloadPlacementProofDoc(order.placementProofDocs[0])">
                          <v-icon small left>mdi-download</v-icon>
                          Comprovante
                        </v-btn>
                      </template>
                      <v-btn
                        v-else
                        color="primary"
                        small
                        depressed
                        @click.stop="openComprobatorioList(order)">
                        <v-icon small left>mdi-cloud-upload-outline</v-icon>
                        Comprobatórios
                      </v-btn>
                    </div>

                    <div class="order-col order-col--status">
                      <v-chip
                        v-if="order.placementProofDocs && order.placementProofDocs.length > 0"
                        color="#4caf50"
                        text-color="white"
                        small>
                        <strong>Aprovado</strong>
                      </v-chip>
                      <v-chip
                        v-else-if="order.statusGlobal"
                        :color="getStatusColor(order.statusGlobal)"
                        text-color="white"
                        small>
                        <strong>{{ order.statusGlobal }}</strong>
                      </v-chip>
                      <span v-else class="grey--text text-body-2">—</span>
                    </div>

                  </div>
                </template>

              </div>
            </td>
          </template>

        </v-data-table>
      </v-card>
    </v-col>
  </v-card>

  <v-dialog v-model="comprobatorioListDialog" max-width="640" @click:outside="closeListDialog">
    <v-card style="display: flex; flex-direction: column; max-height: 82vh; overflow: hidden;">

      
      <v-card-title class="d-flex align-center justify-space-between" style="flex-shrink:0;">
        <div class="d-flex align-center" style="gap:6px;">
          <v-btn v-if="selectedSlot" icon small @click="backToSlotList">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <v-icon v-else color="primary">mdi-file-document-multiple-outline</v-icon>

          <template v-if="!selectedSlot">
            <span class="text-h6">Comprobatórios</span>
          </template>
          <template v-else>
            <v-chip
              x-small
              :color="getSlotTypeColor(selectedSlot.type)"
              dark class="mr-1">
              {{ getSlotTypeLabel(selectedSlot.type) }}
            </v-chip>
            <span class="text-h6">{{ formatDate(selectedSlot.date) }}</span>
          </template>
        </div>
        <v-btn icon @click="closeListDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      
      <v-card-subtitle v-if="!selectedSlot" class="pt-3 pb-2 grey--text text--darken-1" style="flex-shrink:0;">
        Período: <strong>{{ selectedOrder && selectedOrder.period }}</strong>
        &nbsp;·&nbsp;
        Documento: <strong>{{ selectedOrder && (selectedOrder.documentId || '—') }}</strong>
      </v-card-subtitle>

      
      <div v-else class="px-4 pt-3 pb-2" style="flex-shrink:0;">
        <div class="d-flex align-center flex-wrap" style="gap:12px;">
          <div class="d-flex align-center" style="gap:4px;">
            <v-icon x-small color="grey darken-1">mdi-pound</v-icon>
            <span class="text-caption grey--text text--darken-1">Plano</span>
            <span class="text-caption font-weight-bold">#{{ selectedOrder && selectedOrder.gridId }}</span>
          </div>
          <div class="d-flex align-center" style="gap:4px;">
            <v-icon x-small color="grey darken-1">mdi-file-document-outline</v-icon>
            <span class="text-caption grey--text text--darken-1">P.I</span>
            <span class="text-caption font-weight-bold">{{ selectedOrder && (selectedOrder.documentId || '—') }}</span>
          </div>
          <div class="d-flex align-center" style="gap:4px;">
            <v-icon x-small color="grey darken-1">mdi-radio-tower</v-icon>
            <span class="text-caption font-weight-bold">{{ selectedOrder && (selectedOrder.publisherName || '—') }}</span>
          </div>
          <v-chip x-small outlined color="grey darken-1">
            {{ selectedSlot.files ? selectedSlot.files.length : 0 }} arquivo(s)
          </v-chip>
        </div>
      </div>

      <v-divider />

      <v-progress-linear v-if="listLoading || slotsRefreshing" indeterminate color="primary" />

      
      <v-card-text class="py-3" style="overflow-y: auto; min-height: 0;">

        <div v-if="listLoading" class="d-flex align-center justify-center py-8">
          <v-progress-circular indeterminate color="primary" size="32" />
        </div>

        
        <template v-else-if="!selectedSlot">
          <div
            v-if="!slotsData || !slotsData.groups || slotsData.groups.length === 0"
            class="d-flex flex-column align-center justify-center grey--text py-8"
            style="gap:12px;">
            <v-icon size="52" color="grey lighten-1">mdi-cloud-upload-outline</v-icon>
            <span class="text-body-1">Nenhum comprobatório disponível.</span>
          </div>

          <template v-else>
            <template v-for="(group, gIndex) in slotsData.groups">
              <template v-for="({ slot, slotType }, sIdx) in getGroupSlots(group)">
                <div :key="`${gIndex}_${slotType}_${sIdx}`" class="mb-2">
                  <v-tooltip top :disabled="isSlotActive(slot.date)">
                    <template v-slot:activator="{ on, attrs }">
                      <div
                        v-bind="attrs"
                        v-on="on"
                        class="slot-row d-flex align-center justify-space-between"
                        :class="isSlotActive(slot.date) ? 'slot-row--active' : 'slot-row--inactive'"
                        @click="isSlotActive(slot.date) && selectSlot(slot, slotType)">
                        <div class="d-flex align-center" style="gap:10px;">
                          <v-chip x-small :color="getSlotTypeColor(slotType)" dark>
                            {{ getSlotTypeLabel(slotType) }}
                          </v-chip>
                          <div>
                            <div class="text-body-2 font-weight-medium">{{ formatDate(slot.date) }}</div>
                            <div class="text-caption grey--text">
                              {{ slot.files && slot.files.length
                                ? slot.files.length + ' arquivo(s) enviado(s)'
                                : 'Nenhum arquivo enviado' }}
                            </div>
                          </div>
                        </div>
                        <div class="d-flex align-center" style="gap:6px;">
                          <v-chip
                            v-if="slot.files && slot.files.length"
                            x-small color="primary" dark>
                            {{ slot.files.length }}
                          </v-chip>
                          <v-icon v-if="isSlotActive(slot.date)" small color="grey darken-1">mdi-chevron-right</v-icon>
                          <v-icon v-else small color="grey lighten-1">mdi-lock-clock</v-icon>
                        </div>
                      </div>
                    </template>
                    <span>Disponível para envio a partir de {{ formatDate(slot.date) }}</span>
                  </v-tooltip>
                </div>
              </template>
            </template>
          </template>
        </template>

        <template v-else>
          <div v-if="slotsRefreshing" class="d-flex align-center justify-center py-8">
            <v-progress-circular indeterminate color="primary" size="40" />
          </div>

          <div
            v-else-if="!selectedSlot.files || selectedSlot.files.length === 0"
            class="d-flex flex-column align-center justify-center grey--text py-8"
            style="gap:12px;">
            <v-icon size="52" color="grey lighten-1">mdi-cloud-upload-outline</v-icon>
            <span class="text-body-1">Nenhum comprobatório enviado ainda.</span>
            <span class="text-body-2">Faça o envio do seu primeiro comprobatório.</span>
          </div>

          <v-list v-else-if="!slotsRefreshing" dense class="pa-0">
            <v-list-item
              v-for="(file, fi) in selectedSlot.files"
              :key="file._id ? file._id.$oid : fi"
              class="comprobatorio-item mb-2 pa-3"
              style="align-items: flex-start;">
              <v-list-item-avatar size="38" color="primary lighten-5" class="mt-1">
                <v-icon small color="primary">{{ getFileIcon(file._type) }}</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="text-body-2 font-weight-medium mb-1">
                  {{ file.name || '—' }}
                </v-list-item-title>
                <div class="d-flex align-center flex-wrap" style="gap:6px;">
                  <v-chip x-small outlined color="grey darken-1">
                    {{ file._type || 'desconhecido' }}
                  </v-chip>
                  <span class="text-caption grey--text">
                    <v-icon x-small color="grey">mdi-calendar-outline</v-icon>
                    {{ formatDateTime(file.gpp && file.gpp.createdOn) }}
                  </span>
                </div>
              </v-list-item-content>
              <v-list-item-action class="mt-1 d-flex flex-row align-center" style="gap:6px;">
                <v-tooltip top v-if="file.gpp && file.gpp.note" max-width="280">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on">
                      <v-icon small color="red darken-1">mdi-message-alert-outline</v-icon>
                    </v-btn>
                  </template>
                  <div>
                    <div class="font-weight-bold mb-1">Motivo da reprovação:</div>
                    <span>{{ file.gpp.note }}</span>
                  </div>
                </v-tooltip>
                <v-tooltip top v-if="file.url">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on" @click="downloadFile(file)">
                      <v-icon small color="grey darken-1">mdi-download</v-icon>
                    </v-btn>
                  </template>
                  <span>Baixar arquivo</span>
                </v-tooltip>
                <v-chip
                  x-small
                  :color="getStatusColor(getStatusLabel(file.gpp && file.gpp.status))"
                  text-color="white">
                  {{ getStatusLabel(file.gpp && file.gpp.status) }}
                </v-chip>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </template>

      </v-card-text>

      <v-divider />

      
      <v-card-actions class="pa-4" style="flex-shrink:0;">
        <template v-if="selectedSlot">
          <v-btn text @click="backToSlotList">
            <v-icon small left>mdi-arrow-left</v-icon>
            Voltar
          </v-btn>
          <v-spacer />
          <v-btn color="primary" depressed @click="openUploadModal(selectedSlot.type)">
            <v-icon small left>mdi-plus</v-icon>
            Adicionar comprobatório
          </v-btn>
        </template>
        <template v-else>
          <v-spacer />
          <v-btn text @click="closeListDialog">Fechar</v-btn>
        </template>
      </v-card-actions>

    </v-card>
  </v-dialog>

  
  <v-dialog v-model="uploadDialog" max-width="500" :persistent="uploadLoading" @click:outside="!uploadLoading && closeUploadDialog()">
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <div class="d-flex align-center" style="gap:8px;">
          <v-icon color="primary">mdi-cloud-upload-outline</v-icon>
          <span class="text-h6">Enviar comprobatório</span>
        </div>
        <v-btn icon :disabled="uploadLoading" @click="closeUploadDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pt-5 pb-3">
        <FileUpload
          :key="uploadKey"
          @set-upload="onFilesUpdated" />
        <v-textarea
          v-model="noteSender"
          label="Observação (opcional)"
          placeholder="Adicione uma observação sobre este comprobatório…"
          outlined
          dense
          rows="3"
          auto-grow
          hide-details
          class="mt-4" />
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-btn text :disabled="uploadLoading" @click="closeUploadDialog">Cancelar</v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          depressed
          :disabled="!pendingUploadFiles.length"
          :loading="uploadLoading"
          @click="openConfirmOrSubmit">
          <v-icon small left>mdi-send</v-icon>
          Enviar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="confirmUploadDialog" max-width="420" persistent>
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <div class="d-flex align-center" style="gap:8px;">
          <span class="text-h6">Confirmar envio</span>
        </div>
        <v-btn icon @click="confirmUploadDialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pt-4 pb-2">
        <p class="text-body-1 mb-3">
          Tem certeza que deseja enviar este comprobatório?
        </p>
        <div class="text-body-1 grey--text text--darken-2 mb-3">
          Após o envio, o arquivo <strong class="grey--text text--darken-4">não poderá ser alterado ou excluído</strong>.
          Ele será encaminhado para <strong class="grey--text text--darken-4">análise imediatamente</strong>.
        </div>
        <v-checkbox
          v-model="dontShowAgainToday"
          dense
          hide-details
          class="mt-1"
          label="Entendi, não mostrar mais hoje" />
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-btn text @click="confirmUploadDialog = false">Cancelar</v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          depressed
          :loading="uploadLoading"
          @click="confirmAndSubmit">
          <v-icon small left>mdi-check</v-icon>
          Sim, enviar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  </div>

</template>

<script>
import FileUpload from '@/interface/FileUpload.vue';

export default {
  name: 'PisProbative',
  components: { FileUpload },

  data() {
    return {
      plans: [],
      loading: false,
      searchText: '',
      selectedStatus: 'all',
      appliedStatus: 'all',
      selectedPublisher: 'all',
      appliedPublisher: 'all',
      expanded: [],

      comprobatorioListDialog: false,
      uploadDialog: false,
      selectedOrder: null,
      pendingUploadFiles: [],
      uploadLoading: false,
      uploadKey: 0,
      listLoading: false,
      confirmUploadDialog: false,
      dontShowAgainToday: false,
      xpathDownloading: {},
      slotsData: null,
      expandedSlots: {},
      selectedSlotType: null,
      selectedSlot: null,
      slotsRefreshing: false,
      noteSender: '',

      statusOptions: [
        { text: 'Todos', value: 'all' },
        { text: 'Pendente', value: 'Pendente' },
        { text: 'Em aprovação', value: 'Em aprovação' },
        { text: 'Aprovado', value: 'Aprovado' },
        { text: 'Reprovado', value: 'Reprovado' },
        { text: 'Parcialmente pendente', value: 'Parcialmente pendente' },
        { text: 'Parcialmente reprovado', value: 'Parcialmente reprovado' },
      ],

      headers: [
        { text: '', value: 'data-table-expand', width: '48px' },
        { text: 'Número do plano', value: 'gridId', align: 'center', width: '140px' },
        { text: 'Inventário', value: 'publisherName', align: 'center' },
        { text: 'Quantidade de PIs', value: 'piCount', align: 'center', width: '160px' },
        // { text: 'Status', value: 'status', align: 'center', width: '160px' },
      ],
    };
  },

  computed: {
    hasFilterChanges() {
      return this.selectedStatus !== this.appliedStatus ||
             this.selectedPublisher !== this.appliedPublisher;
    },

    publisherOptions() {
      const map = new Map();
      this.plans.forEach(p => {
        (p.publishers || []).forEach(pub => {
          if (!map.has(pub.name)) {
            map.set(pub.name, { text: pub.name, value: pub.name });
          }
        });
      });
      const unique = [...map.values()];
      unique.sort((a, b) => a.text.localeCompare(b.text));
      return [{ text: 'Todos os inventários', value: 'all' }, ...unique];
    },

    filteredPlans() {
      let result = [...this.plans];

      if (this.appliedStatus !== 'all') {
        result = result.filter(p =>
          (p.orders || []).some(o => o.statusGlobal === this.appliedStatus)
        );
      }

      if (this.appliedPublisher && this.appliedPublisher !== 'all') {
        result = result.filter(p =>
          (p.publishers || []).some(pub => pub.name === this.appliedPublisher)
        );
      }

      if (this.searchText && this.searchText.trim()) {
        const search = this.searchText.toLowerCase().trim();
        result = result.filter(p =>
          p.gridId?.toString().includes(search) ||
          (p.publishers || []).some(pub => pub.name?.toLowerCase().includes(search))
        );
      }

      return result;
    },
  },

  mounted() {
    this.fetchPlans().then(() => this.handleQueryParams());
  },

  methods: {
    async handleQueryParams() {
      const { grid, uri, type } = this.$route.query || {};
      if (!grid || !uri) return;

      const gridId = String(grid);
      const plan = this.plans.find(p => String(p.gridId) === gridId);
      if (!plan) return;

      const order = plan.orders.find(o => o.publisherUri === uri);
      if (!order) return;

      const matchedPublisher = (plan.publishers || []).find(p => p.uri === uri);
      if (matchedPublisher && matchedPublisher.name) {
        this.selectedPublisher = matchedPublisher.name;
        this.appliedPublisher = matchedPublisher.name;
      }

      await this.openComprobatorioList(order);

      if (type && this.slotsData && this.slotsData.groups) {
        groupLoop: for (const group of this.slotsData.groups) {
          if (type === 'intermediate') {
            for (const intSlot of (group.intermediates || [])) {
              if (this.isSlotActive(intSlot.date)) {
                this.selectSlot(intSlot, 'intermediate');
                break groupLoop;
              }
            }
          } else {
            const slot = group[type];
            if (slot && this.isSlotActive(slot.date)) {
              this.selectSlot(slot, type);
              break groupLoop;
            }
          }
        }
      }

      const url = new URL(window.location.href);
      url.searchParams.delete('grid');
      url.searchParams.delete('uri');
      url.searchParams.delete('type');
      window.history.replaceState(history.state, '', url.toString());
    },

    applyFilters() {
      this.appliedStatus = this.selectedStatus;
      this.appliedPublisher = this.selectedPublisher || 'all';
    },

    clearFilters() {
      this.searchText = '';
      this.selectedStatus = 'all';
      this.appliedStatus = 'all';
      this.selectedPublisher = 'all';
      this.appliedPublisher = 'all';
    },

    async downloadXpath(order) {
      if (!order.xpath) return;
      this.$set(this.xpathDownloading, order.piId, true);
      try {
        const { data } = await this.$axios.get('security/presigned/link', {
          params: { url: order.xpath, expires: 5, region: order.xpath.includes('tmt-s3-v2')?'us-east-1' : 'sa-east-1'},
        });
        const url = data?.data;
        if (!url) throw new Error('URL não retornada');
        window.open(url, '_blank');
      } catch (error) {
        console.error('Erro ao gerar link de download:', error);
      } finally {
        this.$set(this.xpathDownloading, order.piId, false);
      }
    },

    getStatusColor(status) {
      const colorMap = {
        'Pendente': '#ff9800',
        'Em aprovação': '#2196f3',
        'Aprovado': '#4caf50',
        'Reprovado': '#f44336',
        'Parcialmente pendente': '#9c27b0',
        'Parcialmente reprovado': '#ff5722',
      };
      return colorMap[status] || '#9e9e9e';
    },

    formatPeriod(dateStr) {
      if (!dateStr) return '—';
      const [year, month] = dateStr.split('-');
      const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
      return `${months[parseInt(month, 10) - 1]}/${year}`;
    },

    formatAsuFormat(format) {
      if (!format) return null;
      const parts = [format.type, format.value, format.metric].filter(p => p != null && p !== '');
      return parts.join(' ') || null;
    },

    formatAsusBucket(bucket) {
      if(!bucket) return null
      const parts = [bucket.tilte, bucket.name]
      return parts.join(' ') || null
    },

    formatCnpj(value) {
      if (!value) return '—';
      const digits = String(value).replace(/\D/g, '');
      if (digits.length !== 14) return value;
      return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    },

    async openComprobatorioList(order) {
      this.selectedOrder = order;
      this.slotsData = null;
      this.expandedSlots = {};
      this.comprobatorioListDialog = true;
      this.listLoading = true;
      try {
        const { data } = await this.$axios.get(
          `grids/probative/notification/slots/${order.gridId}/${order.publisherUri}`,
          { params: { startDate: order.startAtDate } }
        );
        this.slotsData = data.data || null;
      } catch (error) {
        console.error('Erro ao buscar comprobatórios:', error);
        this.slotsData = null;
      } finally {
        this.listLoading = false;
      }
    },

    closeListDialog() {
      this.comprobatorioListDialog = false;
      this.selectedOrder = null;
      this.slotsData = null;
      this.expandedSlots = {};
      this.selectedSlot = null;
      this.listLoading = false;
    },

    selectSlot(slot, slotType) {
      this.selectedSlot = { ...slot, type: slotType };
    },

    backToSlotList() {
      this.selectedSlot = null;
    },

    getGroupSlots(group) {
      const slots = [];
      if (group.start) slots.push({ slot: group.start, slotType: 'start' });
      (group.intermediates || []).forEach(s => slots.push({ slot: s, slotType: 'intermediate' }));
      if (group.end) slots.push({ slot: group.end, slotType: 'end' });
      return slots;
    },

    getSlotTypeColor(slotType) {
      if (slotType === 'start') return 'blue darken-1';
      if (slotType === 'intermediate') return 'teal darken-1';
      return 'deep-orange darken-1';
    },

    getSlotTypeLabel(slotType) {
      if (slotType === 'start') return 'Início';
      if (slotType === 'intermediate') return 'Parcial';
      return 'Fim';
    },

    isSlotActive(dateStr) {
      return true; // TESTE: trava de data desativada temporariamente
      // eslint-disable-next-line no-unreachable
      /* --- CÓDIGO ORIGINAL (remover o return true acima para reativar) ---
      if (!dateStr) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const slotDate = new Date(dateStr);
      slotDate.setHours(0, 0, 0, 0);
      return slotDate <= today;
      ------------------------------------------------------------------- */
    },

    toggleSlot(key) {
      this.$set(this.expandedSlots, key, !this.expandedSlots[key]);
    },

    formatDate(dateStr) {
      if (!dateStr) return '—';
      const [year, month, day] = dateStr.split('-');
      const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
      return `${day}/${months[parseInt(month, 10) - 1]}/${year}`;
    },

    getStatusLabel(code) {
      const map = { 0: 'Pendente', 1: 'Em aprovação', 2: 'Aprovado', 3: 'Reprovado' };
      return map[code] ?? 'Pendente';
    },

    getFileIcon(contentType) {
      if (!contentType) return 'mdi-file-outline';
      if (contentType.includes('pdf')) return 'mdi-file-pdf-box';
      if (contentType.includes('image')) return 'mdi-file-image-outline';
      if (contentType.includes('spreadsheet') || contentType.includes('excel') || contentType.includes('csv')) return 'mdi-file-excel-outline';
      if (contentType.includes('word') || contentType.includes('document')) return 'mdi-file-word-outline';
      if (contentType.includes('zip') || contentType.includes('compressed')) return 'mdi-zip-box-outline';
      return 'mdi-file-outline';
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '—';
      const date = new Date(dateStr);
      if (isNaN(date)) return dateStr;
      return date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },

    downloadFile(file) {
      if (!file || !file.url) return;
      const link = document.createElement('a');
      link.href = file.url;
      link.target = '_blank';
      link.download = file.name || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    downloadPlacementProofDoc(doc) {
      if (!doc || !doc.file || !doc.file.url) return;
      const link = document.createElement('a');
      link.href = doc.file.url;
      link.target = '_blank';
      link.download = doc.file.name || 'comprovante';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    openConfirmOrSubmit() {
      const suppressed = localStorage.getItem('suppressProbativeConfirm');
      const today = new Date().toDateString();
      if (suppressed === today) {
        this.submitUpload();
      } else {
        this.dontShowAgainToday = false;
        this.confirmUploadDialog = true;
      }
    },

    confirmAndSubmit() {
      if (this.dontShowAgainToday) {
        localStorage.setItem('suppressProbativeConfirm', new Date().toDateString());
      }
      this.submitUpload();
    },

    openUploadModal(slotType) {
      this.selectedSlotType = slotType || null;
      this.pendingUploadFiles = [];
      this.uploadDialog = true;
    },

    closeUploadDialog() {
      this.uploadDialog = false;
      this.pendingUploadFiles = [];
      this.noteSender = '';
      this.uploadKey++;
    },

    onFilesUpdated(files) {
      this.pendingUploadFiles = files || [];
    },

    async submitUpload() {
      if (!this.pendingUploadFiles.length || !this.selectedOrder) return;

      const currentOrder = this.selectedOrder;
      const type = this.selectedSlotType || (currentOrder.startAtDate === currentOrder.rawPeriod ? 'start' : 'end');
      const notePayload = this.noteSender && this.noteSender.trim() ? { noteSender: this.noteSender.trim() } : {};

      const fileIds = this.pendingUploadFiles.map(f => f._id).filter(Boolean)
      if (!fileIds.length) return;

      this.uploadLoading = true;
      try {
        await this.$axios.post('grids/probative/order?cdv', {
          gridId: currentOrder.gridId,
          publisherUri: currentOrder.publisherUri,
          mongoFileIds: fileIds,
          type,
          slotDate: this.selectedSlot?.date || null,
          ...notePayload,
        });

        this.closeUploadDialog();
        this.confirmUploadDialog = false;

        this.slotsRefreshing = true;
        const slotTypeBefore = this.selectedSlotType;
        try {
          const { data: slotsRefresh } = await this.$axios.get(
            `grids/probative/notification/slots/${currentOrder.gridId}/${currentOrder.publisherUri}`,
            { params: { startDate: currentOrder.startAtDate } }
          );
          this.slotsData = slotsRefresh.data || null;
          // keep selectedSlot in sync if user is still in detail view
          if (this.selectedSlot && this.slotsData && this.slotsData.groups) {
            const slotType = this.selectedSlot.type;
            const slotDate = this.selectedSlot.date;
            groupSync: for (const group of this.slotsData.groups) {
              if (slotType === 'intermediate') {
                for (const intSlot of (group.intermediates || [])) {
                  if (intSlot.date === slotDate) {
                    this.selectedSlot = { ...intSlot, type: 'intermediate' };
                    break groupSync;
                  }
                }
              } else {
                const refreshed = group[slotType];
                if (refreshed && refreshed.date === slotDate) {
                  this.selectedSlot = { ...refreshed, type: slotType };
                  break groupSync;
                }
              }
            }
          }
        } finally {
          this.slotsRefreshing = false;
        }
      } catch (error) {
        console.error('Erro ao enviar comprobatório:', error);
        this.confirmUploadDialog = false;
      } finally {
        this.uploadLoading = false;
      }
    },

    mergeConsecutiveOrders(orders) {
      if (!orders || orders.length === 0) return orders;

      const sorted = [...orders].sort((a, b) => {
        const aStart = a.startAtDate || a.rawPeriod || '';
        const bStart = b.startAtDate || b.rawPeriod || '';
        return aStart < bStart ? -1 : aStart > bStart ? 1 : 0;
      });

      const result = [];
      for (const order of sorted) {
        const { sku, startAtDate, endAtDate } = order;
        if (!sku || !startAtDate || !endAtDate) {
          result.push({ ...order, endPeriod: null, mergedDownloads: [{ piId: order.piId, xpath: order.xpath, period: order.period, documentId: order.documentId }] });
          continue;
        }
        const existing = result.find(r => r.sku === sku && r._mergedEnd === startAtDate);
        if (existing) {
          existing._mergedEnd = endAtDate;
          existing.endPeriod = order.period;
          existing.mergedDownloads.push({ piId: order.piId, xpath: order.xpath, period: order.period, documentId: order.documentId });
        } else {
          result.push({ ...order, _mergedEnd: endAtDate, endPeriod: null, mergedDownloads: [{ piId: order.piId, xpath: order.xpath, period: order.period, documentId: order.documentId }] });
        }
      }

      return result.map(({ _mergedEnd, ...r }) => r);
    },

    async fetchPlans() {
      this.loading = true;
      try {
        const { data } = await this.$axios.get('grids/pis');
        const raw = data.data || data || [];

        const groups = {};
        raw.forEach(pi => {
          const key = String(pi.gridId);
          if (!groups[key]) {
            groups[key] = {
              groupKey: key,
              gridId: pi.gridId,
              publishers: [],
              status: null,
              pis: [],
            };
          }
          const g = groups[key];
          g.pis.push(pi);
          if (!g.publishers.some(p => p.uri === pi.publisherUri)) {
            g.publishers.push({
              name: pi.publisherName,
              uri: pi.publisherUri,
              logo: pi.publisher?.logo || null,
            });
          }
        });

        this.plans = Object.values(groups).map(g => ({
          ...g,
          piCount: g.pis.length,
          orders: g.pis
            .slice()
            .sort((a, b) => (a.period || '').localeCompare(b.period || ''))
            .map(pi => ({
            piId: pi.piId,
            documentId: pi.documentId,
            period: this.formatPeriod(pi.period),
            rawPeriod: pi.period || null,
            format: this.formatAsuFormat(pi.asus?.[0]?.asus?.format),
            bucket: this.formatAsusBucket(pi.asus?.[0]?.asus?.bucket?.[0]),
            publisherName: pi.publisherName,
            publisherLogo: pi.publisher?.logo || null,
            gridProfileDisplayName: pi.gridProfileDisplayName || null,
            providerDocument: pi.providerDocument || null,
            gridProfileLogo: pi.gridProfileLogo || null,
            statusGlobal: pi.statusGlobal || null,
            xpath: pi.xpath,
            gridId: g.gridId,
            ordId: pi.ordId,
            publisherUri: pi.publisherUri || null,
            sku: pi.orders?.[0]?.sku || null,
            startAtDate: pi.orders?.[0]?.startAtDate || null,
            endAtDate: pi.orders?.[0]?.endAtDate || null,
            placementProofDocs: pi.placementProofDocs || [],
          })),
        })).sort((a, b) => Number(b.gridId) - Number(a.gridId));
      } catch (error) {
        console.error('Erro ao buscar comprobatórios:', error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
* {
  font-family: 'Roboto', sans-serif !important;
}


.clickable-rows >>> tbody tr {
  cursor: pointer;
}

.clickable-rows >>> tbody tr:hover {
  background-color: #f5f8ff !important;
}


.clickable-rows >>> tbody tr.v-data-table__expanded__row {
  background-color: #eef3ff !important;
  border-left: 3px solid var(--v-primary-base);
}


.expanded-cell {
  cursor: default;
  background-color: #f9faff !important;
}


.expand-icon {
  transition: transform 0.25s ease;
}
.expand-icon.expanded {
  transform: rotate(180deg);
}


.clickable-rows >>> .v-btn--icon.v-btn--plain {
  background: transparent !important;
}
.clickable-rows >>> .v-btn--icon.v-btn--plain::before {
  background: transparent !important;
  opacity: 0 !important;
}


.orders-wrapper {
  padding: 8px 24px 12px;
  border-left: 3px solid var(--v-primary-base);
  margin: 0;
}


.order-row {
  padding: 10px 16px;
  border-radius: 8px;
  margin-bottom: 6px;
  background-color: #fff;
  border: 1px solid #e8ecf4;
  transition: box-shadow 0.2s ease;
}

.order-row:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.order-row--header {
  background-color: transparent !important;
  border: none !important;
  box-shadow: none !important;
  padding: 4px 16px 4px;
  margin-bottom: 2px;
}

.order-row--last {
  margin-bottom: 0;
}


.order-col--publisher     { flex: 1.5 1 0; min-width: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; }
.order-col--period        { flex: 1 1 0; min-width: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; }
.order-col--docid         { flex: 1.5 1 0; min-width: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; }
.order-col--formats       { flex: 1 1 0; min-width: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; }
.order-col--provider-name { flex: 1.5 1 0; min-width: 0; display: flex; flex-direction: column; align-items: flex-start; justify-content: center; }

.provider-name-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  min-width: 0;
}
.order-col--provider-doc  { flex: 1.2 1 0; min-width: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; }
.order-col--download      { flex: 0 0 40px; display: flex; flex-direction: column; align-items: center; justify-content: center; }
.order-col--action        { flex: 1.2 1 0; min-width: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; }
.order-col--status        { flex: 1 1 0; min-width: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; }
.order-col--status >>> .v-chip { white-space: nowrap; }


.order-col__label {
  display: none;
  font-size: 10px;
  line-height: 1.2;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #9e9e9e;
  margin-bottom: 2px;
}


.order-row--wrap {
  flex-wrap: wrap;
  gap: 6px 0;
}


.order-col--info-icon {
  display: none !important;
}

@media (max-width: 1380px) {
  .order-col--info-icon {
    display: inline-flex !important;
  }
}

.order-info-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 2px 0;
  border-bottom: 1px solid #f0f0f0;
}
.order-info-row:last-child { border-bottom: none; }
.order-info-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  color: #9e9e9e;
  min-width: 70px;
  padding-top: 2px;
}

@media (min-width: 1481px) { .order-info--formats { display: none; } }
@media (min-width: 1321px) { .order-info--doc     { display: none; } }
@media (min-width: 1181px) { .order-info--period  { display: none; } }
@media (min-width: 1091px)  { .order-info--docid   { display: none; } }
@media (min-width: 921px)  { .order-info--publisher { display: none; } }

@media (max-width: 1580px) {
  .order-col--formats,
  .order-row--header > .order-col--formats { display: none !important; }
}

@media (max-width: 1420px) {
  .order-col--provider-doc,
  .order-row--header > .order-col--provider-doc { display: none !important; }
}

@media (max-width: 1280px) {
  .order-col--period,
  .order-row--header > .order-col--period { display: none !important; }
}

@media (max-width: 1160px) {
  .order-col--docid,
  .order-row--header > .order-col--docid { display: none !important; }
}

@media (max-width: 1020px) {
  .order-col--publisher,
  .order-row--header > .order-col--publisher { display: none !important; }
}

@media (max-width: 880px) {
  .orders-wrapper { padding: 6px 8px 10px; }

  .order-row--wrap > .order-col--provider-name,
  .order-row--wrap > .order-col--download,
  .order-row--wrap > .order-col--action,
  .order-row--wrap > .order-col--status,
  .order-row--header { display: none !important; }

  .order-row--mobile-compact { display: flex !important; }

  .order-row--wrap {
    flex-wrap: nowrap;
    gap: 0;
    padding: 10px !important;
  }
}


.v-data-table >>> .v-data-table__wrapper {
  overflow: visible;
}

.primary--text >>> input {
  color: var(--v-primary-base) !important;
}

.primary--text >>> .v-select__selection {
  color: var(--v-primary-base) !important;
}

.comprobatorio-item {
  border: 1px solid #e8ecf4;
  border-radius: 8px;
  background: #fff;
}

.slot-row {
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid #e8ecf4;
  background: #fff;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.slot-row--active {
  cursor: pointer;
}

.slot-row--active:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-color: var(--v-primary-base);
}

.slot-row--inactive {
  cursor: not-allowed;
  opacity: 0.5;
  background-color: #f5f5f5;
}
</style>
